import React, { useState, useMemo } from 'react';
import { Line } from 'react-chartjs-2';
import { useApp } from '../../contexts/AppContext';
import { calculateSingleProperty, formatValue, formatPercent } from '../../utils';
import { PropertyInputs } from '../../types';

const Statistik: React.FC = () => {
  const { currentInputs, portfolio } = useApp();
  
  const [sensitivityParam, setSensitivityParam] = useState<keyof PropertyInputs>('angebotspreis');
  const [rangePercentage, setRangePercentage] = useState(20);
  const [activeView, setActiveView] = useState<'portfolio' | 'sensitivity'>('portfolio');

  const sensitivityParameters = [
    { key: 'angebotspreis', label: 'Angebotspreis', unit: '€' },
    { key: 'kaltmieteProQm', label: 'Kaltmiete pro m²', unit: '€/m²' },
    { key: 'zinssatz', label: 'Zinssatz', unit: '%' },
    { key: 'tilgung', label: 'Tilgung', unit: '%' },
    { key: 'hausgeldUmlagefaehig', label: 'Hausgeld', unit: '€' },
    { key: 'instandhaltung', label: 'Instandhaltungsrücklage', unit: '€' },
    { key: 'wertzuwachsProJahr', label: 'Wertsteigerung', unit: '%' },
    { key: 'finanzierungsanteil', label: 'Finanzierungsanteil', unit: '%' }
  ];

  // Portfolio statistics calculations
  const portfolioStats = useMemo(() => {
    if (portfolio.length === 0) return null;

    // Calculate statistics for each property
    const propertyStats = portfolio.map(property => {
      const calc = property.calculations || calculateSingleProperty(property.inputs);
      return {
        property,
        calc,
        stadtgebiet: property.inputs.stadtgebiet,
        lage: property.inputs.lage,
        schwierigkeit: property.inputs.schwierigkeit,
        baujahr: property.inputs.baujahr,
        stellplatzTyp: property.inputs.stellplatzTyp,
        istData: {
          kaufpreis: calc.kaufpreisIst,
          preisProQm: calc.quadratmeterpreisIst,
          rendite: calc.bruttomietrenditeIst,
          eigenkapitalrendite: calc.eigenkapitalrenditeJahr1Ist,
          cashflow: calc.monatlicherCashflowJahr1IstNachSteuer
        },
        sollData: {
          kaufpreis: calc.kaufpreisSoll,
          preisProQm: calc.quadratmeterpreisSoll,
          rendite: calc.bruttomietrenditeSoll,
          eigenkapitalrendite: calc.eigenkapitalrenditeJahr1Soll,
          cashflow: calc.monatlicherCashflowJahr1SollNachSteuer
        }
      };
    });

    // Calculate averages for IST vs SOLL
    const avgIst = {
      kaufpreis: propertyStats.reduce((sum, s) => sum + s.istData.kaufpreis, 0) / propertyStats.length,
      preisProQm: propertyStats.reduce((sum, s) => sum + s.istData.preisProQm, 0) / propertyStats.length,
      rendite: propertyStats.reduce((sum, s) => sum + s.istData.rendite, 0) / propertyStats.length,
      eigenkapitalrendite: propertyStats.reduce((sum, s) => sum + s.istData.eigenkapitalrendite, 0) / propertyStats.length,
      cashflow: propertyStats.reduce((sum, s) => sum + s.istData.cashflow, 0) / propertyStats.length
    };

    const avgSoll = {
      kaufpreis: propertyStats.reduce((sum, s) => sum + s.sollData.kaufpreis, 0) / propertyStats.length,
      preisProQm: propertyStats.reduce((sum, s) => sum + s.sollData.preisProQm, 0) / propertyStats.length,
      rendite: propertyStats.reduce((sum, s) => sum + s.sollData.rendite, 0) / propertyStats.length,
      eigenkapitalrendite: propertyStats.reduce((sum, s) => sum + s.sollData.eigenkapitalrendite, 0) / propertyStats.length,
      cashflow: propertyStats.reduce((sum, s) => sum + s.sollData.cashflow, 0) / propertyStats.length
    };

    // Group by various categories
    const byStadtgebiet = groupBy(propertyStats, 'stadtgebiet');
    const byLage = groupBy(propertyStats, 'lage');
    const bySchwierigkeit = groupBy(propertyStats, 'schwierigkeit');
    const byBaujahr = propertyStats.reduce((acc, stat) => {
      const decade = Math.floor(stat.baujahr / 10) * 10;
      const key = decade < 1960 ? 'Vor 1960' : `${decade}er`;
      if (!acc[key]) acc[key] = [];
      acc[key].push(stat);
      return acc;
    }, {} as Record<string, typeof propertyStats>);
    const byStellplatz = groupBy(propertyStats, 'stellplatzTyp');

    return {
      propertyStats,
      avgIst,
      avgSoll,
      byStadtgebiet,
      byLage,
      bySchwierigkeit,
      byBaujahr,
      byStellplatz
    };
  }, [portfolio]);

  // Helper function to group properties
  function groupBy<T extends Record<string, any>>(array: T[], key: string): Record<string, T[]> {
    return array.reduce((result, item) => {
      const group = item[key] || 'Unbekannt';
      if (!result[group]) result[group] = [];
      result[group].push(item);
      return result;
    }, {} as Record<string, T[]>);
  }

  // Calculate averages for a group
  function calculateGroupAverages(group: any[]) {
    const avgIst = {
      preisProQm: group.reduce((sum, s) => sum + s.istData.preisProQm, 0) / group.length,
      rendite: group.reduce((sum, s) => sum + s.istData.rendite, 0) / group.length
    };
    const avgSoll = {
      preisProQm: group.reduce((sum, s) => sum + s.sollData.preisProQm, 0) / group.length,
      rendite: group.reduce((sum, s) => sum + s.sollData.rendite, 0) / group.length
    };
    return { avgIst, avgSoll, count: group.length };
  }

  const sensitivityData = useMemo(() => {
    const steps = 11; // -50% to +50% in 10% steps
    const stepSize = rangePercentage * 2 / (steps - 1);
    const results = [];
    
    const baseValue = currentInputs[sensitivityParam as keyof PropertyInputs];
    
    for (let i = 0; i < steps; i++) {
      const multiplier = 1 + ((i * stepSize - rangePercentage) / 100);
      const modifiedInputs = {
        ...currentInputs,
        [sensitivityParam]: typeof baseValue === 'number' ? baseValue * multiplier : baseValue
      };
      
      const calc = calculateSingleProperty(modifiedInputs);
      results.push({
        percentChange: (multiplier - 1) * 100,
        value: modifiedInputs[sensitivityParam as keyof PropertyInputs],
        cashflow: calc.cashflowMonatlichNachSteuer,
        roi: calc.eigenkapitalrendite,
        eigenkapital: calc.eigenkapitalSoll,
        vermoegen10: calc.projectionData[10]?.vermoegen || 0
      });
    }
    
    return results;
  }, [currentInputs, sensitivityParam, rangePercentage]);

  // Chart data
  const cashflowChartData = {
    labels: sensitivityData.map(d => `${formatValue(d.percentChange, '%')}`),
    datasets: [{
      label: 'Monatlicher Cashflow',
      data: sensitivityData.map(d => d.cashflow),
      borderColor: '#3B82F6',
      backgroundColor: 'rgba(59, 130, 246, 0.2)',
      tension: 0.4
    }]
  };

  const roiChartData = {
    labels: sensitivityData.map(d => `${formatValue(d.percentChange, '%')}`),
    datasets: [{
      label: 'Eigenkapitalrendite',
      data: sensitivityData.map(d => d.roi),
      borderColor: '#10B981',
      backgroundColor: 'rgba(16, 185, 129, 0.2)',
      tension: 0.4
    }]
  };

  const vermoegenChartData = {
    labels: sensitivityData.map(d => `${formatValue(d.percentChange, '%')}`),
    datasets: [{
      label: 'Vermögen nach 10 Jahren',
      data: sensitivityData.map(d => d.vermoegen10),
      borderColor: '#8B5CF6',
      backgroundColor: 'rgba(139, 92, 246, 0.2)',
      tension: 0.4
    }]
  };

  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        position: 'top' as const,
      },
      tooltip: {
        callbacks: {
          title: function(context: any) {
            const param = sensitivityParameters.find(p => p.key === sensitivityParam);
            const value = sensitivityData[context[0].dataIndex].value;
            return `${param?.label}: ${formatValue(value as number, param?.unit || '')}`;
          },
          label: function(context: any) {
            const label = context.dataset.label || '';
            const value = context.parsed.y;
            const unit = label.includes('Rendite') ? '%' : '€';
            return `${label}: ${formatValue(value, unit)}`;
          }
        }
      }
    },
    scales: {
      x: {
        title: {
          display: true,
          text: 'Änderung in %'
        }
      },
      y: {
        title: {
          display: true,
          text: 'Wert'
        },
        ticks: {
          callback: function(this: any, value: any) {
            return formatValue(value, '€');
          }
        }
      }
    }
  };

  // Break-even analysis
  const breakEvenAnalysis = useMemo(() => {
    const results = [];
    
    // Find break-even for cashflow
    for (let mult = 0.5; mult <= 1.5; mult += 0.01) {
      const modifiedInputs = {
        ...currentInputs,
        [sensitivityParam]: typeof currentInputs[sensitivityParam as keyof PropertyInputs] === 'number' 
          ? (currentInputs[sensitivityParam as keyof PropertyInputs] as number) * mult
          : currentInputs[sensitivityParam as keyof PropertyInputs]
      };
      
      const calc = calculateSingleProperty(modifiedInputs);
      
      if (Math.abs(calc.cashflowMonatlichNachSteuer) < 5) {
        results.push({
          type: 'Cashflow Break-Even',
          value: modifiedInputs[sensitivityParam as keyof PropertyInputs],
          percentChange: (mult - 1) * 100
        });
        break;
      }
    }
    
    return results;
  }, [currentInputs, sensitivityParam]);

  return (
    <div className="tab-content active space-y-6">
      <h2 className="text-2xl font-bold mb-6">📊 Statistische Auswertung</h2>
      
      {/* View Selector */}
      <div className="bg-white p-4 rounded-lg shadow-md">
        <div className="flex space-x-4">
          <button
            onClick={() => setActiveView('portfolio')}
            className={`px-4 py-2 rounded-lg font-medium transition-colors ${
              activeView === 'portfolio' 
                ? 'bg-blue-600 text-white' 
                : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
            }`}
          >
            Portfolio-Statistiken
          </button>
          <button
            onClick={() => setActiveView('sensitivity')}
            className={`px-4 py-2 rounded-lg font-medium transition-colors ${
              activeView === 'sensitivity' 
                ? 'bg-blue-600 text-white' 
                : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
            }`}
          >
            Sensitivitätsanalyse
          </button>
        </div>
      </div>

      {activeView === 'portfolio' && portfolioStats ? (
        <>
          {/* Portfolio Overview IST vs SOLL */}
          <div className="bg-white p-6 rounded-lg shadow-md">
            <h3 className="text-lg font-semibold mb-4">Portfolio-Durchschnittswerte (IST vs. SOLL)</h3>
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Szenario</th>
                    <th className="px-4 py-2 text-right text-xs font-medium text-gray-500 uppercase">Kaufpreis</th>
                    <th className="px-4 py-2 text-right text-xs font-medium text-gray-500 uppercase">€ / m²</th>
                    <th className="px-4 py-2 text-right text-xs font-medium text-gray-500 uppercase">Brutto-Mietrendite</th>
                    <th className="px-4 py-2 text-right text-xs font-medium text-gray-500 uppercase">EK-Rendite (J1)</th>
                    <th className="px-4 py-2 text-right text-xs font-medium text-gray-500 uppercase">Monatl. CF (n.St.)</th>
                    <th className="px-4 py-2 text-right text-xs font-medium text-gray-500 uppercase">Abschlag</th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  <tr className="bg-red-50">
                    <td className="px-4 py-2 font-medium">IST Ø</td>
                    <td className="px-4 py-2 text-right font-mono">{formatValue(portfolioStats.avgIst.kaufpreis, '€')}</td>
                    <td className="px-4 py-2 text-right font-mono">{formatValue(portfolioStats.avgIst.preisProQm, '€/m²')}</td>
                    <td className="px-4 py-2 text-right font-mono">{formatPercent(portfolioStats.avgIst.rendite)}</td>
                    <td className="px-4 py-2 text-right font-mono">{formatPercent(portfolioStats.avgIst.eigenkapitalrendite)}</td>
                    <td className="px-4 py-2 text-right font-mono">{formatValue(portfolioStats.avgIst.cashflow, '€')}</td>
                    <td className="px-4 py-2 text-right font-mono">-</td>
                  </tr>
                  <tr className="bg-green-50">
                    <td className="px-4 py-2 font-medium">SOLL Ø</td>
                    <td className="px-4 py-2 text-right font-mono">{formatValue(portfolioStats.avgSoll.kaufpreis, '€')}</td>
                    <td className="px-4 py-2 text-right font-mono">{formatValue(portfolioStats.avgSoll.preisProQm, '€/m²')}</td>
                    <td className="px-4 py-2 text-right font-mono">{formatPercent(portfolioStats.avgSoll.rendite)}</td>
                    <td className="px-4 py-2 text-right font-mono">{formatPercent(portfolioStats.avgSoll.eigenkapitalrendite)}</td>
                    <td className="px-4 py-2 text-right font-mono">{formatValue(portfolioStats.avgSoll.cashflow, '€')}</td>
                    <td className="px-4 py-2 text-right font-mono">
                      {formatPercent(((portfolioStats.avgSoll.kaufpreis - portfolioStats.avgIst.kaufpreis) / portfolioStats.avgIst.kaufpreis) * 100)}
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>

          {/* Two column layout for grouped statistics */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Auswertung nach Stadtgebiet */}
            <div className="bg-white p-6 rounded-lg shadow-md">
              <h3 className="text-lg font-semibold mb-4">Auswertung nach Stadtgebiet</h3>
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Stadtgebiet</th>
                      <th className="px-3 py-2 text-center text-xs font-medium text-gray-500 uppercase">Anzahl</th>
                      <th className="px-3 py-2 text-right text-xs font-medium text-gray-500 uppercase">Ø Preis/m² (IST)</th>
                      <th className="px-3 py-2 text-right text-xs font-medium text-gray-500 uppercase">Ø Preis/m² (SOLL)</th>
                      <th className="px-3 py-2 text-right text-xs font-medium text-gray-500 uppercase">Ø Rendite (IST)</th>
                      <th className="px-3 py-2 text-right text-xs font-medium text-gray-500 uppercase">Ø Rendite (SOLL)</th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {Object.entries(portfolioStats.byStadtgebiet).map(([stadtgebiet, properties]) => {
                      const stats = calculateGroupAverages(properties);
                      return (
                        <tr key={stadtgebiet}>
                          <td className="px-3 py-2 text-sm">{stadtgebiet}</td>
                          <td className="px-3 py-2 text-center text-sm">{stats.count}</td>
                          <td className="px-3 py-2 text-right text-sm font-mono bg-red-50">{formatValue(stats.avgIst.preisProQm, '€')}</td>
                          <td className="px-3 py-2 text-right text-sm font-mono bg-green-50">{formatValue(stats.avgSoll.preisProQm, '€')}</td>
                          <td className="px-3 py-2 text-right text-sm font-mono bg-red-50">{formatPercent(stats.avgIst.rendite)}</td>
                          <td className="px-3 py-2 text-right text-sm font-mono bg-green-50">{formatPercent(stats.avgSoll.rendite)}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>

            {/* Auswertung nach Lage */}
            <div className="bg-white p-6 rounded-lg shadow-md">
              <h3 className="text-lg font-semibold mb-4">Auswertung nach Lage</h3>
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Lage</th>
                      <th className="px-3 py-2 text-center text-xs font-medium text-gray-500 uppercase">Anzahl</th>
                      <th className="px-3 py-2 text-right text-xs font-medium text-gray-500 uppercase">Ø Preis/m² (IST)</th>
                      <th className="px-3 py-2 text-right text-xs font-medium text-gray-500 uppercase">Ø Preis/m² (SOLL)</th>
                      <th className="px-3 py-2 text-right text-xs font-medium text-gray-500 uppercase">Ø Rendite (IST)</th>
                      <th className="px-3 py-2 text-right text-xs font-medium text-gray-500 uppercase">Ø Rendite (SOLL)</th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {Object.entries(portfolioStats.byLage).map(([lage, properties]) => {
                      const stats = calculateGroupAverages(properties);
                      return (
                        <tr key={lage}>
                          <td className="px-3 py-2 text-sm">{lage}</td>
                          <td className="px-3 py-2 text-center text-sm">{stats.count}</td>
                          <td className="px-3 py-2 text-right text-sm font-mono bg-red-50">{formatValue(stats.avgIst.preisProQm, '€')}</td>
                          <td className="px-3 py-2 text-right text-sm font-mono bg-green-50">{formatValue(stats.avgSoll.preisProQm, '€')}</td>
                          <td className="px-3 py-2 text-right text-sm font-mono bg-red-50">{formatPercent(stats.avgIst.rendite)}</td>
                          <td className="px-3 py-2 text-right text-sm font-mono bg-green-50">{formatPercent(stats.avgSoll.rendite)}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>

            {/* Auswertung nach Schwierigkeit */}
            <div className="bg-white p-6 rounded-lg shadow-md">
              <h3 className="text-lg font-semibold mb-4">Auswertung nach Schwierigkeit</h3>
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Schwierigkeit</th>
                      <th className="px-3 py-2 text-center text-xs font-medium text-gray-500 uppercase">Anzahl</th>
                      <th className="px-3 py-2 text-right text-xs font-medium text-gray-500 uppercase">Ø Preis/m² (IST)</th>
                      <th className="px-3 py-2 text-right text-xs font-medium text-gray-500 uppercase">Ø Preis/m² (SOLL)</th>
                      <th className="px-3 py-2 text-right text-xs font-medium text-gray-500 uppercase">Ø Rendite (IST)</th>
                      <th className="px-3 py-2 text-right text-xs font-medium text-gray-500 uppercase">Ø Rendite (SOLL)</th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {Object.entries(portfolioStats.bySchwierigkeit).map(([schwierigkeit, properties]) => {
                      const stats = calculateGroupAverages(properties);
                      return (
                        <tr key={schwierigkeit}>
                          <td className="px-3 py-2 text-sm">{schwierigkeit}</td>
                          <td className="px-3 py-2 text-center text-sm">{stats.count}</td>
                          <td className="px-3 py-2 text-right text-sm font-mono bg-red-50">{formatValue(stats.avgIst.preisProQm, '€')}</td>
                          <td className="px-3 py-2 text-right text-sm font-mono bg-green-50">{formatValue(stats.avgSoll.preisProQm, '€')}</td>
                          <td className="px-3 py-2 text-right text-sm font-mono bg-red-50">{formatPercent(stats.avgIst.rendite)}</td>
                          <td className="px-3 py-2 text-right text-sm font-mono bg-green-50">{formatPercent(stats.avgSoll.rendite)}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>

            {/* Auswertung nach Baujahresklasse */}
            <div className="bg-white p-6 rounded-lg shadow-md">
              <h3 className="text-lg font-semibold mb-4">Auswertung nach Baujahresklasse</h3>
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Baujahresklasse</th>
                      <th className="px-3 py-2 text-center text-xs font-medium text-gray-500 uppercase">Anzahl</th>
                      <th className="px-3 py-2 text-right text-xs font-medium text-gray-500 uppercase">Ø Preis/m² (IST)</th>
                      <th className="px-3 py-2 text-right text-xs font-medium text-gray-500 uppercase">Ø Preis/m² (SOLL)</th>
                      <th className="px-3 py-2 text-right text-xs font-medium text-gray-500 uppercase">Ø Rendite (IST)</th>
                      <th className="px-3 py-2 text-right text-xs font-medium text-gray-500 uppercase">Ø Rendite (SOLL)</th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {Object.entries(portfolioStats.byBaujahr)
                      .sort(([a], [b]) => a.localeCompare(b))
                      .map(([baujahr, properties]) => {
                        const stats = calculateGroupAverages(properties);
                        return (
                          <tr key={baujahr}>
                            <td className="px-3 py-2 text-sm">{baujahr}</td>
                            <td className="px-3 py-2 text-center text-sm">{stats.count}</td>
                            <td className="px-3 py-2 text-right text-sm font-mono bg-red-50">{formatValue(stats.avgIst.preisProQm, '€')}</td>
                            <td className="px-3 py-2 text-right text-sm font-mono bg-green-50">{formatValue(stats.avgSoll.preisProQm, '€')}</td>
                            <td className="px-3 py-2 text-right text-sm font-mono bg-red-50">{formatPercent(stats.avgIst.rendite)}</td>
                            <td className="px-3 py-2 text-right text-sm font-mono bg-green-50">{formatPercent(stats.avgSoll.rendite)}</td>
                          </tr>
                        );
                      })}
                  </tbody>
                </table>
              </div>
            </div>

            {/* Auswertung nach Stellplatz-Typ */}
            <div className="bg-white p-6 rounded-lg shadow-md lg:col-span-2">
              <h3 className="text-lg font-semibold mb-4">Auswertung nach Stellplatz-Typ</h3>
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Stellplatz-Typ</th>
                      <th className="px-3 py-2 text-center text-xs font-medium text-gray-500 uppercase">Anzahl</th>
                      <th className="px-3 py-2 text-right text-xs font-medium text-gray-500 uppercase">Ø Preis/m² (IST)</th>
                      <th className="px-3 py-2 text-right text-xs font-medium text-gray-500 uppercase">Ø Preis/m² (SOLL)</th>
                      <th className="px-3 py-2 text-right text-xs font-medium text-gray-500 uppercase">Ø Rendite (IST)</th>
                      <th className="px-3 py-2 text-right text-xs font-medium text-gray-500 uppercase">Ø Rendite (SOLL)</th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {Object.entries(portfolioStats.byStellplatz).map(([stellplatz, properties]) => {
                      const stats = calculateGroupAverages(properties);
                      return (
                        <tr key={stellplatz}>
                          <td className="px-3 py-2 text-sm">{stellplatz}</td>
                          <td className="px-3 py-2 text-center text-sm">{stats.count}</td>
                          <td className="px-3 py-2 text-right text-sm font-mono bg-red-50">{formatValue(stats.avgIst.preisProQm, '€')}</td>
                          <td className="px-3 py-2 text-right text-sm font-mono bg-green-50">{formatValue(stats.avgSoll.preisProQm, '€')}</td>
                          <td className="px-3 py-2 text-right text-sm font-mono bg-red-50">{formatPercent(stats.avgIst.rendite)}</td>
                          <td className="px-3 py-2 text-right text-sm font-mono bg-green-50">{formatPercent(stats.avgSoll.rendite)}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </>
      ) : activeView === 'portfolio' ? (
        <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-6">
          <p className="text-gray-700">Keine Immobilien im Portfolio. Fügen Sie Immobilien zum Portfolio hinzu, um Statistiken anzuzeigen.</p>
        </div>
      ) : null}

      {activeView === 'sensitivity' && (
        <>
          {/* Controls */}
          <div className="bg-white p-6 rounded-lg shadow-md">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Parameter für Analyse
                </label>
                <select
                  value={sensitivityParam}
                  onChange={(e) => setSensitivityParam(e.target.value as keyof PropertyInputs)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  {sensitivityParameters.map(param => (
                    <option key={param.key} value={param.key}>
                      {param.label}
                    </option>
                  ))}
                </select>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Variationsbereich: ±{rangePercentage}%
                </label>
                <input
                  type="range"
                  min="10"
                  max="50"
                  value={rangePercentage}
                  onChange={(e) => setRangePercentage(Number(e.target.value))}
                  className="w-full"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Aktueller Wert
                </label>
                <div className="text-lg font-semibold">
                  {formatValue(
                    currentInputs[sensitivityParam as keyof PropertyInputs] as number,
                    sensitivityParameters.find(p => p.key === sensitivityParam)?.unit || ''
                  )}
                </div>
              </div>
            </div>
          </div>

          {/* Break-even Analysis */}
          {breakEvenAnalysis.length > 0 && (
            <div className="bg-white p-6 rounded-lg shadow-md">
              <h3 className="text-lg font-semibold mb-4">Break-Even Punkte</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {breakEvenAnalysis.map((item, index) => (
                  <div key={index} className="p-4 bg-gray-50 rounded-lg">
                    <div className="text-sm text-gray-600">{item.type}</div>
                    <div className="text-lg font-semibold">
                      {formatValue(
                        item.value as number,
                        sensitivityParameters.find(p => p.key === sensitivityParam)?.unit || ''
                      )}
                    </div>
                    <div className="text-sm text-gray-500">
                      ({formatValue(item.percentChange, '%')} Änderung)
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Charts */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <div className="bg-white p-6 rounded-lg shadow-md">
              <h3 className="text-lg font-semibold mb-4">Cashflow Sensitivität</h3>
              <div style={{ height: '300px' }}>
                <Line data={cashflowChartData} options={chartOptions} />
              </div>
            </div>
            
            <div className="bg-white p-6 rounded-lg shadow-md">
              <h3 className="text-lg font-semibold mb-4">ROI Sensitivität</h3>
              <div style={{ height: '300px' }}>
                <Line data={roiChartData} options={chartOptions} />
              </div>
            </div>
            
            <div className="bg-white p-6 rounded-lg shadow-md lg:col-span-2">
              <h3 className="text-lg font-semibold mb-4">Vermögensentwicklung Sensitivität (10 Jahre)</h3>
              <div style={{ height: '400px' }}>
                <Line data={vermoegenChartData} options={chartOptions} />
              </div>
            </div>
          </div>

          {/* Data Table */}
          <div className="bg-white p-6 rounded-lg shadow-md overflow-x-auto">
            <h3 className="text-lg font-semibold mb-4">Detaillierte Werte</h3>
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Änderung
                  </th>
                  <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Wert
                  </th>
                  <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Monatl. Cashflow
                  </th>
                  <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    ROI
                  </th>
                  <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Eigenkapital
                  </th>
                  <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Vermögen (10J)
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {sensitivityData.map((row, index) => (
                  <tr key={index} className={index === Math.floor(sensitivityData.length / 2) ? 'bg-blue-50 font-semibold' : ''}>
                    <td className="px-4 py-2 whitespace-nowrap text-sm">
                      {formatValue(row.percentChange, '%')}
                    </td>
                    <td className="px-4 py-2 whitespace-nowrap text-sm">
                      {formatValue(
                        row.value as number,
                        sensitivityParameters.find(p => p.key === sensitivityParam)?.unit || ''
                      )}
                    </td>
                    <td className={`px-4 py-2 whitespace-nowrap text-sm ${row.cashflow < 0 ? 'text-red-600' : 'text-green-600'}`}>
                      {formatValue(row.cashflow, '€')}
                    </td>
                    <td className="px-4 py-2 whitespace-nowrap text-sm">
                      {formatValue(row.roi, '%')}
                    </td>
                    <td className="px-4 py-2 whitespace-nowrap text-sm">
                      {formatValue(row.eigenkapital, '€')}
                    </td>
                    <td className="px-4 py-2 whitespace-nowrap text-sm">
                      {formatValue(row.vermoegen10, '€')}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}
    </div>
  );
};

export default Statistik;